// =============================================================================
// DiaryViewScreen — /diary-view/:token (PUBLIC, no auth) — T-330
//
// Client-facing hosted SITE DIARY LIBRARY for one project. The unguessable
// token in the URL is the only credential; the api-gateway /diary-view/:token
// validates it (+ 7-day expiry / revocation) server-side and returns the
// project + every included diary entry with pre-signed photos. The client
// browses a clean timeline and downloads per-entry PDFs / photo ZIPs, or the
// whole space at once. Rendered outside the AppShell. Site-diary-centric look.
// =============================================================================

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Download, Images, Loader2, AlertTriangle, FileText, CalendarClock,
  Building2, MapPin, FlagOff, BookOpen,
} from "lucide-react";
import { downloadDiaryPdf } from "./diaryPdf";
import { downloadDiaryPhotosZip } from "./diaryZip";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.coretelgroup.com";

interface Photo { photo_id: string; url: string; caption: string | null; kind: string | null; sort_order: number; }
interface Entry {
  diary_id: string; entry_date: string; scope_done: string | null;
  delays: string | null; delay_billable: boolean; delay_calc: any;
  conditions: any; who_on_site: any; started_at: string | null; ended_at: string | null; outage: any;
  photos: Photo[];
}
interface ViewData {
  share:   { title: string | null; expires_at: string | null };
  project: { project_code: string; project_name: string; client_name: string | null; site_name: string | null } | null;
  entries: Entry[];
}

const KIND_LABELS: Record<string, string> = {
  setup: "Site setup", works: "Works completed", delay: "Delays / issues", plant: "Plant", general: "General",
};
const KIND_ORDER = ["setup", "works", "delay", "plant", "general"];

function fmtDay(iso: string): string {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}
function fmtShort(iso: string): string {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short" }); }
  catch { return iso; }
}
function fmtExpiry(iso: string | null): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}
function safe(s: string): string { return s.replace(/[^A-Za-z0-9]+/g, "_"); }

export default function DiaryViewScreen() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ViewData | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "invalid" | "expired" | "revoked">("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [sort, setSort] = useState<"newest" | "oldest">("newest");   // T-379 — timeline order

  // T-379 — loader extracted so downloads can re-fetch FRESH signed photo URLs.
  // The old flow zipped whatever URLs were loaded at page-open; a tab left open
  // past URL expiry (Steph overnight) fetched nothing → empty zip.
  async function fetchView(): Promise<ViewData | null> {
    const res = await fetch(`${API_BASE_URL}/diary-view/${token}`);
    if (res.status === 410) {
      const b = await res.json().catch(() => ({}));
      setState(b?.error === "revoked" ? "revoked" : "expired");
      return null;
    }
    if (!res.ok) { setState("invalid"); return null; }
    return (await res.json()) as ViewData;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await fetchView();
        if (cancelled || !json) return;
        setData(json); setState("ok");
      } catch { if (!cancelled) setState("invalid"); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fresh URLs for a download; falls back to what's on screen if refetch fails.
  async function freshData(): Promise<ViewData | null> {
    try {
      const json = await fetchView();
      if (json) { setData(json); return json; }
    } catch { /* fall through */ }
    return data;
  }

  function pdfDataFor(e: Entry) {
    return {
      projectCode: data?.project?.project_code ?? "Site",
      projectName: data?.project?.project_name ?? "",
      siteName:    data?.project?.site_name ?? null,
      clientName:  data?.project?.client_name ?? null,
      entryDate:   e.entry_date,
      scopeDone:   e.scope_done,
      delays:      e.delays,
      delayBillable: e.delay_billable,
      delayCalc:   e.delay_calc,
      conditions:  e.conditions,
      whoOnSite:   e.who_on_site,
      startedAt:   e.started_at,
      endedAt:     e.ended_at,
      outage:      e.outage,
      photos:      e.photos.map(p => ({ url: p.url, caption: p.caption, kind: p.kind })),
    };
  }

  async function handlePdf(e: Entry) {
    setBusy("pdf-" + e.diary_id);
    try { await downloadDiaryPdf(pdfDataFor(e)); } catch { /* noop */ } finally { setBusy(null); }
  }
  const ZIP_FAIL_MSG =
    "Couldn't download the photos just now — likely a connection drop. Please try the download again.";

  function reportPartial(r: { zipped: number; failed: { name: string; reason: string }[]; total: number }) {
    if (r.failed.length > 0) {
      // Surface the dominant failure reason — turns a user report into a diagnosis.
      const counts = new Map<string, number>();
      for (const f of r.failed) counts.set(f.reason, (counts.get(f.reason) ?? 0) + 1);
      const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "unknown";
      window.alert(
        `${r.zipped} of ${r.total} photos downloaded — ${r.failed.length} couldn't be fetched (${top}). ` +
        `The zip includes a _MISSING_PHOTOS.txt listing them. Run the download again for a complete set.`,
      );
    }
  }

  async function handleZip(e: Entry) {
    if (e.photos.length === 0) return;
    setBusy("zip-" + e.diary_id);
    try {
      // T-379 — re-fetch fresh signed URLs first, then zip the fresh entry.
      const d = await freshData();
      const fresh = d?.entries.find(x => x.diary_id === e.diary_id) ?? e;
      const base = `${safe(d?.project?.project_code ?? "site")}_${fresh.entry_date}`;
      const r = await downloadDiaryPhotosZip({ filenameBase: base, photos: fresh.photos.map((p, i) => ({ url: p.url, name: `${base}_photo_${i + 1}.jpg` })) });
      reportPartial(r);
    } catch {
      window.alert(ZIP_FAIL_MSG);
    } finally { setBusy(null); }
  }
  async function handleDownloadAll() {
    if (!data) return;
    setBusy("all");
    try {
      // T-379 — fresh URLs for the whole space before the big zip.
      const d = await freshData();
      if (!d) return;
      const code = safe(d.project?.project_code ?? "site");
      const photos: { url: string; name: string }[] = [];
      const seen: Record<string, number> = {};
      for (const e of d.entries) {
        for (const p of e.photos) {
          seen[e.entry_date] = (seen[e.entry_date] ?? 0) + 1;
          photos.push({ url: p.url, name: `${e.entry_date}/${code}_${e.entry_date}_photo_${seen[e.entry_date]}.jpg` });
        }
      }
      if (photos.length === 0) return;
      const r = await downloadDiaryPhotosZip({ filenameBase: `${code}_SiteDiary_AllPhotos`, photos });
      reportPartial(r);
    } catch {
      window.alert(ZIP_FAIL_MSG);
    } finally { setBusy(null); }
  }

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-bg"><Loader2 className="animate-spin text-accent" size={28} /></div>;
  }
  if (state !== "ok" || !data) {
    const msg = state === "expired" ? "This link has expired."
      : state === "revoked" ? "This link has been replaced by a newer one."
      : "This link isn't valid.";
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-bg text-center p-6">
        <AlertTriangle size={32} className="text-accent mb-3" />
        <div className="text-[16px] font-semibold text-text">{msg}</div>
        <div className="text-[13px] text-text-muted mt-1">Ask your Coretel contact for a fresh link.</div>
      </div>
    );
  }

  const totalPhotos = data.entries.reduce((a, e) => a + e.photos.length, 0);
  const title = data.share.title ?? `${data.project?.project_code ?? ""} — Site Diary`;

  return (
    <div className="min-h-screen bg-bg text-text">
      {/* Header band */}
      <div className="bg-accent text-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-10">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider2 text-white/85">
            <BookOpen size={13} /> Coretel · Site Diary
          </div>
          <div className="text-2xl md:text-3xl font-semibold mt-1.5">{title}</div>
          <div className="text-[13px] text-white/90 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {data.project?.project_name && <span>{data.project.project_name}</span>}
            {data.project?.client_name && <span className="inline-flex items-center gap-1.5"><Building2 size={13} /> {data.project.client_name}</span>}
            {data.project?.site_name && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {data.project.site_name}</span>}
          </div>
          <div className="text-[12px] text-white/80 mt-3">
            {data.entries.length} {data.entries.length === 1 ? "entry" : "entries"} · {totalPhotos} photo{totalPhotos === 1 ? "" : "s"}
            {data.share.expires_at && <span className="inline-flex items-center gap-1 ml-2"><CalendarClock size={12} /> available until {fmtExpiry(data.share.expires_at)}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Sort toggle + whole-space download */}
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          {/* T-379 — newest/oldest first */}
          <div className="inline-flex rounded-md border border-line overflow-hidden text-[12px]">
            {(["newest", "oldest"] as const).map(s => (
              <button key={s} onClick={() => setSort(s)}
                className={`px-3 py-1.5 ${sort === s ? "bg-accent text-white" : "bg-bg text-text-muted hover:text-text"}`}>
                {s === "newest" ? "Newest first" : "Oldest first"}
              </button>
            ))}
          </div>
          {totalPhotos > 0 && (
            <button onClick={handleDownloadAll} disabled={busy !== null}
              className="inline-flex items-center gap-2 bg-accent text-white px-4 py-2.5 rounded-md text-[13px] font-medium disabled:opacity-60 hover:bg-accent-hover">
              <Images size={16} /> {busy === "all" ? "Zipping…" : `Download all photos (${totalPhotos})`}
            </button>
          )}
        </div>

        {/* Timeline of entries */}
        <div className="relative pl-6 md:pl-8">
          <div className="absolute left-[7px] md:left-[9px] top-1 bottom-1 w-px bg-line" aria-hidden />
          <div className="space-y-6">
            {[...data.entries].sort((a, b) =>
              sort === "newest" ? b.entry_date.localeCompare(a.entry_date) : a.entry_date.localeCompare(b.entry_date)
            ).map(e => {
              const usedKinds = new Set(e.photos.map(p => p.kind || "general"));
              return (
                <div key={e.diary_id} className="relative">
                  {/* Timeline node */}
                  <div className="absolute -left-6 md:-left-8 top-1.5 flex flex-col items-center">
                    <div className="w-[15px] h-[15px] md:w-[19px] md:h-[19px] rounded-full bg-accent border-4 border-bg" />
                  </div>

                  <div className="bg-ink-card border border-line rounded-lg overflow-hidden">
                    <div className="p-4 md:p-5 space-y-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-[15px] font-semibold text-text">{fmtDay(e.entry_date)}</div>
                          <div className="text-[11px] font-mono text-text-muted">{fmtShort(e.entry_date)}</div>
                        </div>
                        {e.delay_billable && <span className="text-[11px] px-2 py-0.5 rounded-full bg-bid/15 text-bid border border-bid/30">Billable delay</span>}
                      </div>

                      {e.scope_done && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-medium mb-1">Work completed</div>
                          <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{e.scope_done}</div>
                        </div>
                      )}

                      {e.delays && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-medium mb-1 inline-flex items-center gap-1">
                            <FlagOff size={10} /> Delays / blockers{e.delay_billable && <span className="text-bid">· billable</span>}
                          </div>
                          <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{e.delays}</div>
                        </div>
                      )}

                      {/* Photos grouped by kind */}
                      {e.photos.length > 0 && (
                        <div className="space-y-3 pt-1">
                          {KIND_ORDER.map(k => {
                            const list = e.photos.filter(p => (p.kind || "general") === k);
                            if (list.length === 0) return null;
                            return (
                              <div key={k}>
                                {usedKinds.size > 1 && <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-medium mb-1.5">{KIND_LABELS[k]} ({list.length})</div>}
                                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                  {list.map(p => (
                                    <a key={p.photo_id} href={p.url} download target="_blank" rel="noopener noreferrer"
                                      className="block aspect-square bg-ink-raised border border-line rounded overflow-hidden hover:border-accent transition-colors relative group">
                                      <img src={p.url} alt={p.caption ?? ""} className="w-full h-full object-cover" loading="lazy" />
                                      <div className="absolute top-1 right-1 bg-bg/90 rounded p-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity"><Download size={12} className="text-accent" /></div>
                                      {p.caption && <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[10px] px-1.5 py-0.5 truncate">{p.caption}</div>}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Per-entry actions */}
                    <div className="px-4 md:px-5 py-3 border-t border-line bg-ink-raised/40 flex gap-2 flex-wrap">
                      <button onClick={() => handlePdf(e)} disabled={busy !== null}
                        className="inline-flex items-center gap-1.5 bg-accent text-white px-3 py-1.5 rounded text-[12px] font-medium disabled:opacity-60 hover:bg-accent-hover">
                        <FileText size={13} /> {busy === "pdf-" + e.diary_id ? "Building…" : "PDF report"}
                      </button>
                      {e.photos.length > 0 && (
                        <button onClick={() => handleZip(e)} disabled={busy !== null}
                          className="inline-flex items-center gap-1.5 bg-ink-card border border-line px-3 py-1.5 rounded text-[12px] font-medium disabled:opacity-60 hover:border-accent">
                          <Images size={13} /> {busy === "zip-" + e.diary_id ? "Zipping…" : `Photos (${e.photos.length})`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-line text-[11px] text-text-dim text-center">
          Coretel Group Pty Ltd · ops.coretelgroup.com
        </div>
      </div>
    </div>
  );
}
